import React, { useMemo } from 'react';

import { Controller, useFormContext } from 'react-hook-form';

import { FormControl, FormErrorMessage, FormLabel } from '@brand/components';
import { Select as MultiSelect } from 'chakra-react-select';
import {
  IFieldProps,
  IMultiSelectFieldSchema,
  ISelectFieldStyles,
} from 'schema/generator/FormSchema';

import { useErrorMessage } from 'hooks/form/useErrorMessage';
import { useStyles } from 'hooks/form/useStyles';

import ComponentIsVisible from 'components/utils/IsVisible';

export interface ISelectFieldOptionsType<T = number, S = unknown> {
  additional?: S;
  label: string;
  value: T;
}

export const MultiSelectField = ({
  field,
  name,
}: IFieldProps<IMultiSelectFieldSchema>): JSX.Element => {
  const { label, isRequired, shouldDisplay, styles = {} } = field;

  const { control, watch } = useFormContext();
  const fieldStyles = useStyles<ISelectFieldStyles>('selectField', styles);
  const errorMessage = useErrorMessage(name, label);

  const values = watch(name);

  const isVisible = useMemo(() => {
    return shouldDisplay ? shouldDisplay(values) : true;
  }, [values, shouldDisplay]);

  return (
    <ComponentIsVisible when={isVisible}>
      <Controller
        control={control}
        key={`${name}-control`}
        name={name}
        render={({
          field: { name, onBlur, onChange, ref, value },
          fieldState: { error },
        }) => (
          <FormControl
            isInvalid={!!errorMessage}
            isRequired={isRequired}
            {...fieldStyles.control}
          >
            <ComponentIsVisible when={!!label}>
              <FormLabel
                htmlFor={name}
                variant="proRikApp"
                {...fieldStyles.label}
              >
                {label}
              </FormLabel>
            </ComponentIsVisible>
            <MultiSelect
              chakraStyles={{
                multiValue: ({ ...provided }) => ({
                  ...provided,
                  backgroundColor: field.isDisabled
                    ? 'var(--chakra-colors-gray-300)'
                    : 'initial',
                }),
                multiValueRemove: ({ ...provided }) => ({
                  ...provided,
                  display: field.isDisabled ? 'none' : 'initial',
                }),
              }}
              closeMenuOnSelect={false}
              defaultValue={field.options}
              hasStickyGroupHeaders
              hideSelectedOptions={false}
              isClearable={field.isDisabled}
              isDisabled={field.isDisabled}
              isMulti
              isReadOnly={field.isDisabled}
              name={name}
              onBlur={onBlur}
              onChange={onChange}
              options={field.defaultOptions}
              placeholder={field.placeholder}
              ref={ref}
              selectedOptionStyle="check"
              value={value}
            />
            <ComponentIsVisible when={!!error}>
              <FormErrorMessage {...fieldStyles.errorMessage}>
                {error?.message}
              </FormErrorMessage>
            </ComponentIsVisible>
          </FormControl>
        )}
      />
    </ComponentIsVisible>
  );
};
